import type { DealNote } from "@edge/shared";
import { Panel } from "../shared/Panel";

interface NotesPanelProps {
  notes: DealNote[];
  loading: boolean;
}

/**
 * Read-only notes list for focus mode's left column. Zoho note content can
 * contain HTML (rich text); we strip to plain text for safety + readability.
 * Note creation lands in Session 1c via the write-confirm flow.
 */
export function NotesPanel({ notes, loading }: NotesPanelProps) {
  return (
    <Panel className="notes-panel" title="NOTES" count={`${notes.length}`}>
      {loading && notes.length === 0 && (
        <div className="notes-empty">Loading…</div>
      )}
      {!loading && notes.length === 0 && (
        <div className="notes-empty">No notes on this deal yet.</div>
      )}
      {notes.map((n) => (
        <div className="note-item" key={n.id}>
          <div className="note-header">
            <div className="note-title">▸ {(n.title ?? "(no title)").toUpperCase()}</div>
            <div className="note-date">{formatDateTime(n.createdAt)}</div>
          </div>
          <div className="note-body">{stripHtml(n.content)}</div>
          {n.createdBy && <div className="note-author">— {n.createdBy}</div>}
        </div>
      ))}
    </Panel>
  );
}

function stripHtml(s: string): string {
  // Replace block-level tags with newlines, list items with bullets, then
  // strip the rest. Good enough for the underwriter's-notebook view.
  return s
    .replace(/<li[^>]*>/gi, "\n• ")
    .replace(/<\/(p|div|li|ul|ol|br)\s*\/?>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const date = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    month: "short",
    day: "numeric",
  }).format(d);
  const time = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);
  return `${date.toUpperCase()} · ${time}`;
}
